import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { DollarSign, Zap, Code, Users, ArrowRight, Github, Lock, ShieldCheck, BadgeCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { StickyBuyBar } from '@/components/StickyBuyBar';
import { SalesTicker } from '@/components/SalesTicker';
import { SalesToasts } from '@/components/SalesToasts';
import { MilestoneBadge } from '@/components/MilestoneBadge';
import { CountdownOffer } from '@/components/CountdownOffer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [demoMode, setDemoMode] = useState(false);

  useEffect(() => {
    axios.get(`${API}/pricing`)
      .then((res) => setDemoMode(Boolean(res.data?.demo_mode)))
      .catch(() => setDemoMode(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDonate = () => {
    // Navigate to pricing page instead of direct payment
    navigate('/pricing');
  };

  return (
    <div className="relative min-h-screen">
      <div className="circuit-bg" />
      <div className="circuit-overlay" />
      
      <div className="content-wrapper">
        {/* Demo Mode Banner - only shown when backend is actually in demo mode */}
        {demoMode && (
        <div className="bg-green-500/20 border-b border-green-500/50 py-3 px-4" data-testid="demo-banner">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-green-400 text-sm">
              🎮 <strong>DEMO MODE</strong> - Preview the full system without PayPal credentials. 
              <span className="ml-2 text-green-300">Set up real payments by adding your PayPal API keys.</span>
            </p>
          </div>
        </div>
        )}

        {/* Navigation */}
        <nav className="border-b border-green-500/20 backdrop-blur-md" data-testid="main-navigation">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              <div className="flex items-center space-x-3">
                <DollarSign className="w-10 h-10 text-green-500" />
                <span className="text-2xl font-bold glow-text matrix-text">MILLIONAIRE THE MACHINE</span>
              </div>
              <div className="hidden md:flex space-x-8">
                <button
                  onClick={() => navigate('/pricing')}
                  className="text-green-400 hover:text-green-300 transition-colors"
                  data-testid="pricing-nav"
                >
                  Pricing
                </button>
                <button
                  onClick={() => navigate('/how-it-works')}
                  className="text-green-400 hover:text-green-300 transition-colors"
                  data-testid="how-it-works-nav"
                >
                  How It Works
                </button>
                <button
                  onClick={() => navigate('/setup')}
                  className="text-green-400 hover:text-green-300 transition-colors"
                  data-testid="setup-nav"
                >
                  Setup Guide
                </button>
                <button
                  onClick={() => navigate('/pro-dashboard')}
                  className="text-green-400 hover:text-green-300 transition-colors"
                  data-testid="pro-dashboard-nav"
                >
                  Pro Dashboard
                </button>
                <button
                  onClick={() => navigate('/admin')}
                  className="text-green-400 hover:text-green-300 transition-colors"
                  data-testid="admin-nav"
                >
                  Admin
                </button>
              </div>
            </div>
          </div>
        </nav>

        {/* Live social-proof ticker of recent purchases */}
        <SalesTicker />

        {/* Limited-time launch offer with live countdown */}
        <CountdownOffer onClaim={() => navigate('/pricing')} />

        {/* Hero Section */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8" data-testid="hero-section">
          <div className="max-w-7xl mx-auto text-center">
            <div className="scan-line mb-8">
              <h1 className="hero-title glow-text" data-testid="hero-title">
                BECOME A MILLIONAIRE
                <br />
                <span className="text-green-400">THE MACHINE WAY</span>
              </h1>
            </div>

            <MilestoneBadge />
            
            <p className="hero-subtitle text-green-300 max-w-3xl mx-auto mb-12" data-testid="hero-subtitle">
              Open-source wealth generation system. Learn the blueprint. Deploy your instance. 
              Insert your keys. Start earning. It's 100% ethical, legal, and replicable.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
              <Button
                onClick={handleDonate}
                className="btn-primary text-lg px-8 py-6 group"
                data-testid="donate-button"
              >
                View Pricing
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => navigate('/how-it-works')}
                variant="outline"
                className="btn-secondary text-lg px-8 py-6"
                data-testid="learn-more-button"
              >
                Learn More
              </Button>
            </div>

            <p className="text-green-400/80 text-sm text-center mb-8" data-testid="email-note">
              💡 Choose Basic ($1) or Pro ($19 with AI features)
            </p>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto" data-testid="stats-section">
              <div className="card-tech p-6 rounded-lg">
                <div className="text-4xl font-bold text-green-500 glow-text mb-2" data-testid="stat-open-source">100%</div>
                <div className="text-green-300">Open Source</div>
              </div>
              <div className="card-tech p-6 rounded-lg">
                <div className="text-4xl font-bold text-green-500 glow-text mb-2" data-testid="stat-users">∞</div>
                <div className="text-green-300">Potential Users</div>
              </div>
              <div className="card-tech p-6 rounded-lg">
                <div className="text-4xl font-bold text-green-500 glow-text mb-2" data-testid="stat-legal">LEGAL</div>
                <div className="text-green-300">& Ethical</div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="features-section">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 glow-text" data-testid="features-title">
              THE SYSTEM
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <Card className="card-tech p-8 hover:scale-105 transition-transform" data-testid="feature-open-source">
                <Code className="w-12 h-12 text-green-500 mb-4" />
                <h3 className="text-2xl font-bold mb-4 text-green-400">Open Source</h3>
                <p className="text-green-300 leading-relaxed">
                  Complete source code available. Fork it, modify it, make it yours. 
                  No hidden secrets, no locked features.
                </p>
              </Card>

              <Card className="card-tech p-8 hover:scale-105 transition-transform" data-testid="feature-paypal">
                <DollarSign className="w-12 h-12 text-green-500 mb-4" />
                <h3 className="text-2xl font-bold mb-4 text-green-400">PayPal Integration</h3>
                <p className="text-green-300 leading-relaxed">
                  Accept payments instantly. Insert your PayPal API keys and start 
                  collecting $1 from every person who wants the knowledge.
                </p>
              </Card>

              <Card className="card-tech p-8 hover:scale-105 transition-transform" data-testid="feature-ai">
                <Zap className="w-12 h-12 text-green-500 mb-4" />
                <h3 className="text-2xl font-bold mb-4 text-green-400">AI Co-Pilot</h3>
                <p className="text-green-300 leading-relaxed">
                  Pro tier unlocks AI Setup Assistant, Marketing Generator,
                  Business Coach and Documentation tools — powered by your own OpenAI key.
                </p>
              </Card>

              <Card className="card-tech p-8 hover:scale-105 transition-transform" data-testid="feature-replicable">
                <Users className="w-12 h-12 text-green-500 mb-4" />
                <h3 className="text-2xl font-bold mb-4 text-green-400">Replicable Model</h3>
                <p className="text-green-300 leading-relaxed">
                  Every user can become a distributor. The knowledge spreads, 
                  everyone wins. Network effect at its finest.
                </p>
              </Card>

              <Card className="card-tech p-8 hover:scale-105 transition-transform" data-testid="feature-legal">
                <Lock className="w-12 h-12 text-green-500 mb-4" />
                <h3 className="text-2xl font-bold mb-4 text-green-400">100% Legal</h3>
                <p className="text-green-300 leading-relaxed">
                  You're selling information and open-source software licenses. 
                  Fully ethical, completely legal.
                </p>
              </Card>

              <Card className="card-tech p-8 hover:scale-105 transition-transform" data-testid="feature-github">
                <Github className="w-12 h-12 text-green-500 mb-4" />
                <h3 className="text-2xl font-bold mb-4 text-green-400">GitHub Ready</h3>
                <p className="text-green-300 leading-relaxed">
                  Deploy to GitHub Pages, Vercel, or any hosting platform. 
                  Full documentation included.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="cta-section">
          <div className="max-w-4xl mx-auto text-center">
            <div className="card-tech p-12 pulse-glow rounded-xl">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 glow-text" data-testid="cta-title">
                READY TO START?
              </h2>
              <p className="text-xl text-green-300 mb-8 leading-relaxed" data-testid="cta-description">
                For just $1, get instant access to the complete source code, setup instructions, 
                and the knowledge to replicate this system for yourself.
              </p>
              <Button
                onClick={handleDonate}
                disabled={isLoading}
                className="btn-primary text-xl px-12 py-8 group"
                data-testid="cta-button"
              >
                {isLoading ? 'Processing...' : 'Get Instant Access - $1'}
                <ArrowRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-green-500/20 py-12 px-4 sm:px-6 lg:px-8" data-testid="footer">
          <div className="max-w-7xl mx-auto text-center">
            {/* Trust row */}
            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-5 mb-6" data-testid="footer-trust-row">
              <span className="flex items-center gap-1.5 text-green-400 text-xs">
                <Lock className="w-3.5 h-3.5" /> 256-bit SSL
              </span>
              <span className="h-3 w-px bg-green-500/30" />
              <span className="flex items-center gap-1.5 text-green-400 text-xs">
                <ShieldCheck className="w-3.5 h-3.5" /> PayPal Verified
              </span>
              <span className="h-3 w-px bg-green-500/30" />
              <span className="flex items-center gap-1.5 text-green-400 text-xs">
                <BadgeCheck className="w-3.5 h-3.5" /> Site Secured
              </span>
              <span className="h-3 w-px bg-green-500/30" />
              <span className="flex items-center gap-1.5 text-green-400 text-xs">
                <ShieldCheck className="w-3.5 h-3.5" /> PCI DSS Compliant
              </span>
            </div>
            <p className="text-green-400 matrix-text">
              © 2025 MILLIONAIRE THE MACHINE | Open Source | MIT License
            </p>
            <p className="text-green-500/60 mt-2 text-sm">
              Educational purposes only. Results may vary. Do your own research.
            </p>
          </div>
        </footer>

        {/* Spacer so sticky buy bar never covers footer content */}
        <div className="h-24" aria-hidden="true" />
      </div>

      {/* Sticky impulse-buy bar - appears on scroll past hero */}
      <StickyBuyBar />

      {/* Rotating social-proof pop-ups of recent purchases */}
      <SalesToasts />
    </div>
  );
};

export default LandingPage;